import { useState, useCallback } from "react";
import { Talent, User } from "../../types/user";
import { Box, Stack, useTheme, useMediaQuery } from "@mui/material";
import TalentInfo from "./TalentInfo/TalentInfo";
import { AboutMe } from "./AboutMe/AboutMe";
import { Skills } from "./Skills/Skills";
import QueriesList from "./CardList/QueriesList";
import PostList from "./CardList/PostList"; 
import ReviewsList from "./CardList/ReviewsList";
import StageCards from "./StageCards";
import ShareProfileModal from "./ShareProfileModal"; // ✅ Adjust path as needed

const getCurrentPageUrl = (): string => {
  const { origin, pathname, search, hash } = window.location;
  const url = `${origin}${pathname}${search}${hash}`;
  console.log("Current Page URL:", url);
  return url;
};

export default function TalentProfile({
  userInfo,
  talentInfo: currentProfile,
}: {
  userInfo: User | null;
  talentInfo: Talent | null;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const openShareModal = useCallback(() => setIsShareModalOpen(true), []);
  const closeShareModal = useCallback(() => setIsShareModalOpen(false), []);

  // ——— Netflix-ish tokens (design only)
  const COLORS = {
    bg: "#0b0b0b",
    panel: "#141414",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.78)",
    border: "rgba(255,255,255,0.12)",
    red: "#E50914",
  };

  const panelSx = {
    backgroundColor: COLORS.panel,
    color: COLORS.text,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 3,
    p: { xs: 2, sm: 3 },
    boxShadow: "0 16px 40px rgba(0,0,0,0.45)",
  };

  return (
    <Box
      sx={{
        width: "100%",
        position: "relative",
        overflow: "hidden",
        background: COLORS.bg,
        color: COLORS.text,
      }}
    >
      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: isMobile ? "380px" : "520px",
          overflow: "hidden",
        }}
      >
        {/* Hero Image */}
        <img
          loading="lazy"
          src={currentProfile?.cover_image || "/assets/singer_singing.jpg"}
          alt="talent-image"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            position: "absolute",
            inset: 0,
            filter: "saturate(1.05) contrast(1.02)",
          }}
        />

        {/* Cinematic overlays */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 55%, rgba(11,11,11,1) 100%)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(1200px 500px at 10% 20%, rgba(229,9,20,0.12), transparent 60%)",
            pointerEvents: "none",
          }}
        />
      </Box>

      {/* Content container */}
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2.5, sm: 3.5, md: 4 },
          display: "flex",
          flexDirection: "column",
          gap: { xs: 2, sm: 2.5, md: 3 },
        }}
      >
        {/* Talent Info (kept as-is, wrapped for dark panel look) */}
        <Box sx={panelSx}>
          <TalentInfo
            talentInfo={currentProfile!}
            userInfo={userInfo!}
            shareableSlug={getCurrentPageUrl}
            onShareClick={openShareModal}
          />
        </Box>

        {/* About Me + Skills */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 2, sm: 2.5, md: 3 }}
          sx={{ alignItems: "stretch" }}
        >
          <Box sx={{ flex: 1, minWidth: 0, ...panelSx }}>
            <AboutMe
              bio={currentProfile?.bio || ""}
              portfolioLinks={currentProfile?.portfolio_links || []}
              socialMedia={{
                facebook: "https://www.facebook.com/jennyteachmusic",
                tiktok: "https://www.tiktok.com/@jennyteachmusic",
                instagram: "https://www.instagram.com/jennyteachmusic",
                youtube: "https://www.youtube.com/@jennyteachmusic",
              }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, ...panelSx }}>
            <Skills skills={currentProfile?.skills || []} />
          </Box>
        </Stack>

        <Box sx={panelSx}>
          <StageCards talentId={currentProfile?.id ?? ""} />
        </Box>

        {/* Posts + (Reviews & Queries) */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 2, sm: 2.5, md: 3 }}
          sx={{ width: "100%" }}
        >
          <Box sx={{ flex: 1, width: "100%", minWidth: 0, ...panelSx }}>
            <PostList />
          </Box>

          <Stack
            direction="column"
            spacing={{ xs: 2, sm: 2.5, md: 3 }}
            sx={{ flex: 1, width: "100%", minWidth: 0 }}
          >
            <Box sx={panelSx}>
              <ReviewsList />
            </Box>
            <Box sx={panelSx}>
              <QueriesList />
            </Box>
          </Stack>
        </Stack>
      </Box>

      {/* Share Profile Modal */}
      <ShareProfileModal open={isShareModalOpen} onClose={closeShareModal} />
    </Box>
  );
}
